/**
 * loadSchema — resolves a form_id to a parsed FormSchema.
 *
 * All schemas live at src/schema/forms/<form_id>.json. We use Vite's
 * import.meta.glob to bundle them at build time so there is no runtime
 * network fetch. The glob is eager so the JSON is available synchronously
 * after the first call resolves.
 *
 * Usage:
 *   const schema = await loadSchema('47SP_820_onshore_stage1');
 */

import type { FormSchema } from '../schema-renderer/types';
import type { FormId } from '../../schema/generated/index';

// Vite static glob — all form schemas bundled as JSON modules.
const SCHEMA_MODULES = import.meta.glob<{ default: FormSchema }>(
  '../../schema/forms/*.json',
  { eager: false },
);

// Supplementary forms (Form 80) live in their own directory: same schema
// format, rendered by the dedicated Form 80 tab rather than the intake router.
const SUPPLEMENTARY_MODULES = import.meta.glob<{ default: FormSchema }>(
  '../../schema/supplementary-forms/*.json',
  { eager: false },
);

// Cache: form_id → schema (avoids re-parsing on repeat calls)
const cache = new Map<string, FormSchema>();

/**
 * Load a FormSchema by form_id.
 *
 * Throws if the form_id is not found in the bundled schema set.
 */
export async function loadSchema(formId: FormId | string): Promise<FormSchema> {
  if (cache.has(formId)) {
    return cache.get(formId)!;
  }

  const key = `../../schema/forms/${formId}.json`;
  const supplementaryKey = `../../schema/supplementary-forms/${formId}.json`;
  const loader = SCHEMA_MODULES[key] ?? SUPPLEMENTARY_MODULES[supplementaryKey];

  if (!loader) {
    throw new Error(
      `Schema not found: "${formId}". ` +
      `Available schemas: ${Object.keys(SCHEMA_MODULES).map((k) => k.replace('../../schema/forms/', '').replace('.json', '')).join(', ')}`,
    );
  }

  const mod = await loader();
  const schema = mod.default;
  cache.set(formId, schema);
  return schema;
}

/**
 * Return all available form IDs derived from the bundled schema files.
 * Useful for the SchemaPreview admin page.
 */
export function availableFormIds(): string[] {
  return Object.keys(SCHEMA_MODULES).map((k) =>
    k.replace('../../schema/forms/', '').replace('.json', ''),
  );
}
// --- Form routing -----------------------------------------------------------
// Moved to formRouting.ts so plain Node scripts can use it (this module calls
// import.meta.glob at import time, which only Vite can resolve). Re-exported here
// so every existing import site is unaffected.
export {
  pickFormId,
  hasSponsorForm,
  isSponsorRequired,
  sponsorRoleLabel,
  sponsorRoleNoun,
} from './formRouting';
import { pickFormId } from './formRouting';


/**
 * True when the form for this (visa, role) actually carries stage-2 sections.
 *
 * Only the partner-visa forms are genuinely two-stage. 1418, 1149, 1419, 147 and
 * 1006 have NO stage-2 sections at all, so showing those clients a locked
 * "Post-lodgement" step promises a form that can never unlock. Derived from the
 * schema rather than a hardcoded list so it cannot drift from the JSON.
 */
export async function formHasStage2(
  visaSubclass: string,
  isSponsor: boolean,
  variant?: string | null,
): Promise<boolean> {
  const formId = pickFormId(visaSubclass, isSponsor, variant);
  if (!formId) return false;
  const schema = await loadSchema(formId);
  return (schema.sections ?? []).some((s) => s.stage === 2);
}
